import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineView } from "foldkit/submodel";

import type { AdminDetail, AdminUser } from "../../api/commands/admin";
import { toMessage } from "../../api/errors";
import { i18next } from "../../i18n";
import { busyAttributes, busySpinner } from "../../widgets/busy";
import { confirmDialog } from "../../widgets/dialog";
import { reasonText } from "../../widgets/keepInTouchCards";
import type { Shared } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";
import { selectedUser, visibleUsers } from "./update";

const DAY_MS = 86_400_000;

/** "today", "yesterday", "{n}d ago", or the never label. */
export const lastActiveLabel = (
	iso: string | null,
	now: number = Date.now(),
): string => {
	if (iso === null) return i18next.t("admin.never");
	const days = Math.floor((now - Date.parse(iso)) / DAY_MS);
	if (Number.isNaN(days)) return iso;
	if (days <= 0) return i18next.t("admin.today");
	if (days === 1) return i18next.t("admin.yesterday");
	return i18next.t("admin.daysAgo", { count: days });
};

const formatDate = (iso: string): string => {
	const d = new Date(iso);
	return Number.isNaN(d.getTime())
		? iso
		: d.toLocaleDateString(undefined, {
				month: "short",
				day: "numeric",
				year: "numeric",
			});
};

const initials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((w) => w[0]?.toUpperCase() ?? "")
		.join("");

const avatar = (h: HtmlBuilder<Message>, user: AdminUser): Html =>
	user.imageUrl === null
		? h.span(
				[
					h.Class(
						"w-8 h-8 rounded-full bg-primary text-primary-content grid place-items-center text-xs font-semibold shrink-0",
					),
					h.AriaHidden(true),
				],
				[initials(user.name)],
			)
		: h.img([
				h.Src(user.imageUrl),
				h.Alt(""),
				h.Class("w-8 h-8 rounded-full object-cover shrink-0"),
			]);

const digestSwitch = (h: HtmlBuilder<Message>, user: AdminUser): Html =>
	h.input([
		h.Type("checkbox"),
		h.Class("toggle toggle-primary toggle-sm"),
		h.Checked(user.digestSubscribed),
		h.AriaLabel(i18next.t("admin.digestFor", { name: user.name })),
		h.OnClick(
			Message.ToggledDigest({
				email: user.email,
				subscribed: !user.digestSubscribed,
			}),
		),
	]);

const userRow = (
	h: HtmlBuilder<Message>,
	user: AdminUser,
	selected: boolean,
): Html =>
	h.tr(
		[
			h.Key(user.circleId),
			h.Attribute("aria-selected", String(selected)),
			h.Class(selected ? "bg-primary/10" : "hover:bg-base-200/60"),
		],
		[
			h.td(
				[h.Class("p-0")],
				[
					h.button(
						[
							h.Type("button"),
							h.Class("flex items-center gap-3 w-full text-left px-3 py-2"),
							h.AriaLabel(user.name),
							h.OnClick(Message.ClickedUser({ circleId: user.circleId })),
						],
						[
							avatar(h, user),
							h.div(
								[h.Class("min-w-0")],
								[
									h.div([h.Class("font-medium leading-tight")], [user.name]),
									h.div(
										[
											h.Class(
												"font-mono text-xs text-base-content/60 truncate",
											),
										],
										[user.email],
									),
								],
							),
						],
					),
				],
			),
			h.td([h.Class("text-sm")], [user.circleName]),
			h.td(
				[h.Class("text-sm whitespace-nowrap")],
				[lastActiveLabel(user.lastActiveAt)],
			),
			h.td(
				[h.Class("text-sm text-right tabular-nums")],
				[String(user.records)],
			),
			h.td(
				[h.Class("text-sm text-right tabular-nums")],
				[String(user.entries)],
			),
			h.td([], [digestSwitch(h, user)]),
		],
	);

const sparkline = (h: HtmlBuilder<Message>, counts: ReadonlyArray<number>) => {
	const w = 300;
	const ht = 40;
	const max = Math.max(1, ...counts);
	const step = counts.length > 1 ? w / (counts.length - 1) : w;
	const pts = counts.map(
		(v, i) => [i * step, ht - 4 - (v / max) * (ht - 8)] as const,
	);
	const line = pts
		.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`)
		.join(" ");
	const last = pts[pts.length - 1];
	return h.svg(
		[
			h.Attribute("xmlns", "http://www.w3.org/2000/svg"),
			h.ViewBox(`0 0 ${w} ${ht}`),
			h.Class("w-full h-10 block"),
			h.Role("img"),
			h.AriaLabel(
				i18next.t("admin.sparkline", {
					count: counts.reduce((a, n) => a + n, 0),
				}),
			),
		],
		[
			h.path(
				[
					h.Attribute("d", `${line} L${w} ${ht} L0 ${ht} Z`),
					h.Fill("currentColor"),
					h.Class("text-primary/15"),
				],
				[],
			),
			h.path(
				[
					h.Attribute("d", line),
					h.Fill("none"),
					h.Stroke("currentColor"),
					h.Attribute("stroke-width", "1.5"),
					h.Class("text-primary"),
				],
				[],
			),
			last === undefined
				? h.empty
				: h.circle(
						[
							h.Attribute("cx", last[0].toFixed(1)),
							h.Attribute("cy", last[1].toFixed(1)),
							h.Attribute("r", "2.5"),
							h.Fill("currentColor"),
							h.Class("text-primary"),
						],
						[],
					),
		],
	);
};

const kv = (h: HtmlBuilder<Message>, label: string, value: string): Html[] => [
	h.dt([h.Class("text-base-content/60")], [label]),
	h.dd([h.Class("m-0 tabular-nums")], [value]),
];

const queue = (h: HtmlBuilder<Message>, detail: AdminDetail): Html => {
	const cards = detail.week.cards.filter((c) => !c.cleared);
	const tasks = detail.week.openTasks.length;
	if (cards.length === 0 && tasks === 0)
		return h.p(
			[h.Class("text-sm text-base-content/60")],
			[i18next.t("admin.nothingDue")],
		);
	return h.ul(
		[h.Class("space-y-1 text-sm list-none p-0 m-0")],
		[
			...cards.map((c) =>
				h.li(
					[h.Key(c.record.id), h.Class("flex justify-between gap-3")],
					[
						h.span([], [c.record.name]),
						h.span(
							[h.Class("text-xs text-base-content/60 text-right")],
							[reasonText(c.reason)],
						),
					],
				),
			),
			tasks === 0
				? h.empty
				: h.li(
						[h.Class("text-base-content/70")],
						[i18next.t("admin.openTasks", { count: tasks })],
					),
		],
	);
};

const drawer = (
	h: HtmlBuilder<Message>,
	model: Model,
	user: AdminUser,
): Html => {
	const detail = AsyncData.getOrElse(model.detail, () => null);
	const canSend =
		detail !== null &&
		(detail.week.cards.some((c) => !c.cleared) ||
			detail.week.openTasks.length > 0);
	return h.aside(
		[
			h.Class(
				"rounded-2xl border border-base-300 bg-base-100 shadow-sm lg:sticky lg:top-4",
			),
			h.AriaLabel(user.name),
		],
		[
			h.div(
				[h.Class("flex items-center gap-3 p-4 border-b border-base-300")],
				[
					avatar(h, user),
					h.div(
						[h.Class("min-w-0")],
						[
							h.h2([h.Class("font-semibold leading-tight")], [user.name]),
							h.div(
								[h.Class("font-mono text-xs text-base-content/60 truncate")],
								[user.email],
							),
						],
					),
				],
			),
			h.dl(
				[
					h.Class(
						"grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 p-4 text-sm border-b border-base-300",
					),
				],
				[
					...kv(h, i18next.t("admin.columns.circle"), user.circleName),
					...kv(
						h,
						i18next.t("admin.columns.joined"),
						formatDate(user.joinedAt),
					),
					...kv(
						h,
						i18next.t("admin.columns.lastActive"),
						lastActiveLabel(user.lastActiveAt),
					),
					...kv(h, i18next.t("admin.columns.records"), String(user.records)),
					...kv(h, i18next.t("admin.columns.entries"), String(user.entries)),
					h.dt(
						[h.Class("text-base-content/60")],
						[i18next.t("admin.columns.digest")],
					),
					h.dd(
						[h.Class("m-0 flex items-center gap-2")],
						[
							digestSwitch(h, user),
							user.digestSubscribed
								? i18next.t("admin.digestOn")
								: i18next.t("admin.digestOff"),
						],
					),
				],
			),
			h.div(
				[h.Class("p-4 border-b border-base-300 space-y-2")],
				[
					h.h3(
						[h.Class("text-xs uppercase tracking-wider text-base-content/60")],
						[i18next.t("admin.sections.entries")],
					),
					detail === null
						? h.div([h.Class("skeleton h-10 w-full")], [])
						: sparkline(h, detail.entriesByWeek),
				],
			),
			h.div(
				[h.Class("p-4 border-b border-base-300 space-y-2")],
				[
					h.h3(
						[h.Class("text-xs uppercase tracking-wider text-base-content/60")],
						[i18next.t("admin.sections.queue")],
					),
					AsyncData.isFailure(model.detail)
						? h.p(
								[h.Role("alert"), h.Class("text-sm text-error")],
								[toMessage(model.detail.error)],
							)
						: detail === null
							? h.div([h.Class("skeleton h-12 w-full")], [])
							: queue(h, detail),
				],
			),
			h.div(
				[h.Class("p-4 space-y-2")],
				[
					h.h3(
						[h.Class("text-xs uppercase tracking-wider text-base-content/60")],
						[i18next.t("admin.sections.actions")],
					),
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-primary btn-sm w-full justify-between"),
							...busyAttributes(h, model.sending, !canSend),
							h.AriaLabel(i18next.t("admin.sendReminder")),
							h.OnClick(Message.ClickedSendReminder()),
						],
						[
							busySpinner(h, model.sending),
							i18next.t("admin.sendReminder"),
							h.span(
								[h.Class("text-xs opacity-70 font-normal")],
								[
									detail === null
										? ""
										: canSend
											? i18next.t("admin.sendHint")
											: i18next.t("admin.queueEmpty"),
								],
							),
						],
					),
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-sm w-full"),
							h.AriaLabel(i18next.t("admin.copyEmail")),
							h.OnClick(Message.ClickedCopyEmail()),
						],
						[
							model.copied
								? i18next.t("admin.copied")
								: i18next.t("admin.copyEmail"),
						],
					),
					Option.match(model.feedback, {
						onNone: () => h.empty,
						onSome: (f) =>
							h.p(
								[
									h.Role("status"),
									h.Class(
										f.kind === "success"
											? "text-sm text-success"
											: "text-sm text-error",
									),
								],
								[f.message],
							),
					}),
				],
			),
		],
	);
};

export const view = defineView<Model, Message, Shared>((model, _shared, h) => {
	const users = visibleUsers(model);
	const selected = selectedUser(model);
	const th = (label: string, extra = "") =>
		h.th(
			[
				h.Class(
					`text-xs uppercase tracking-wider font-medium text-base-content/60 ${extra}`,
				),
			],
			[label],
		);
	return h.div(
		[
			h.DataAttribute("testid", "page-Admin"),
			h.Class("max-w-6xl mx-auto space-y-4"),
		],
		[
			h.div(
				[],
				[
					h.h1(
						[h.Class("text-2xl font-display font-medium")],
						[i18next.t("admin.title")],
					),
					h.p(
						[h.Class("text-sm text-base-content/60")],
						[i18next.t("admin.subtitle")],
					),
				],
			),
			h.input([
				h.Type("search"),
				h.Class("input input-bordered input-sm w-full max-w-xs"),
				h.Placeholder(i18next.t("admin.search")),
				h.AriaLabel(i18next.t("admin.search")),
				h.Value(model.search),
				h.OnInput((search) => Message.ChangedSearch({ search })),
			]),
			h.div(
				[h.Class("grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px] items-start")],
				[
					h.div(
						[
							h.Class(
								"rounded-2xl border border-base-300 bg-base-100 overflow-x-auto",
							),
						],
						[
							AsyncData.isFailure(model.users)
								? h.p(
										[h.Role("alert"), h.Class("p-4 text-sm text-error")],
										[toMessage(model.users.error)],
									)
								: AsyncData.isLoading(model.users)
									? h.div(
											[h.Class("p-4 space-y-2")],
											[
												h.div([h.Class("skeleton h-8 w-full")], []),
												h.div([h.Class("skeleton h-8 w-full")], []),
											],
										)
									: h.table(
											[h.Class("table table-sm")],
											[
												h.thead(
													[],
													[
														h.tr(
															[],
															[
																th(i18next.t("admin.columns.user")),
																th(i18next.t("admin.columns.circle")),
																th(i18next.t("admin.columns.lastActive")),
																th(
																	i18next.t("admin.columns.records"),
																	"text-right",
																),
																th(
																	i18next.t("admin.columns.entries"),
																	"text-right",
																),
																th(i18next.t("admin.columns.digest")),
															],
														),
													],
												),
												h.tbody(
													[],
													users.length === 0
														? [
																h.tr(
																	[],
																	[
																		h.td(
																			[
																				h.Attribute("colspan", "6"),
																				h.Class("text-sm text-base-content/60"),
																			],
																			[i18next.t("admin.noUsers")],
																		),
																	],
																),
															]
														: users.map((u) =>
																userRow(
																	h,
																	u,
																	Option.contains(model.selected, u.circleId),
																),
															),
												),
											],
										),
						],
					),
					Option.match(selected, {
						onNone: () =>
							h.aside(
								[
									h.Class(
										"rounded-2xl border border-dashed border-base-300 p-6 text-sm text-base-content/60",
									),
									h.AriaLabel(i18next.t("admin.pickUser")),
								],
								[i18next.t("admin.pickUser")],
							),
						onSome: (user) => drawer(h, model, user),
					}),
				],
			),
			Option.match(selected, {
				onNone: () => h.empty,
				onSome: (user) =>
					confirmDialog(h, {
						slotId: "confirm-reminder",
						model: model.dialog,
						toParentMessage: (message) => Message.GotDialogMessage({ message }),
						title: i18next.t("admin.confirm.title"),
						message: i18next.t("admin.confirm.message", { email: user.email }),
						cancelLabel: i18next.t("admin.confirm.cancel"),
						confirmLabel: i18next.t("admin.confirm.send"),
						onConfirm: Message.ClickedConfirmSend(),
						isPending: model.sending,
					}),
			}),
		],
	);
});
